import React, { useState } from 'react';
import { Play, FileText, Sparkles, Users, Zap } from 'lucide-react';

const SandboxChoiceModal = ({ onChoice }) => {
  const [selectedChoice, setSelectedChoice] = useState(null);

  const handleChoice = (choice) => {
    setSelectedChoice(choice);
    setTimeout(() => {
      onChoice(choice);
    }, 300);
  };

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-[#111113] border border-white/10 rounded-2xl shadow-2xl max-w-4xl w-full p-8 animate-in fade-in slide-in-from-bottom-4 duration-500">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="w-16 h-16 bg-gradient-to-br from-violet-500 to-purple-600 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg shadow-violet-500/20">
            <Zap className="w-8 h-8 text-white" />
          </div>
          <h2 className="text-3xl font-bold text-white mb-2">
            Velkommen til Lexy! 🎉
          </h2>
          <p className="text-lg text-neutral-400">
            Hvordan vil du komme i gang?
          </p>
        </div>

        {/* Choices */}
        <div className="grid md:grid-cols-2 gap-6">
          {/* Demo Choice */}
          <button
            onClick={() => handleChoice('demo')}
            className={`relative group p-6 rounded-xl border transition-all duration-300 text-left hover:scale-[1.02] ${
              selectedChoice === 'demo'
                ? 'border-violet-500/50 bg-violet-500/10'
                : 'border-white/10 bg-white/[0.02] hover:border-white/20 hover:bg-white/[0.04]'
            }`}
          >
            {/* Recommended Badge */}
            <div className="absolute -top-3 right-4">
              <span className="bg-gradient-to-r from-amber-500 to-orange-500 text-white text-xs font-bold px-3 py-1 rounded-full shadow-lg">
                ⭐ Anbefalt
              </span>
            </div>

            <div className="flex items-start gap-4 mb-4">
              <div className="w-12 h-12 bg-gradient-to-br from-violet-500 to-purple-600 rounded-xl flex items-center justify-center flex-shrink-0">
                <Play className="w-6 h-6 text-white" />
              </div>
              <div>
                <h3 className="text-xl font-bold text-white mb-1">
                  🎭 Prøv ferdig demo
                </h3>
                <p className="text-sm text-neutral-400">
                  Se Lexy i aksjon med én gang
                </p>
              </div>
            </div>

            <ul className="space-y-2.5 mb-4">
              <li className="flex items-start gap-2 text-sm text-neutral-300">
                <Sparkles className="w-4 h-4 text-violet-400 flex-shrink-0 mt-0.5" />
                <span>3 demo-saker med ekte data og AI-analyse</span>
              </li>
              <li className="flex items-start gap-2 text-sm text-neutral-300">
                <Users className="w-4 h-4 text-violet-400 flex-shrink-0 mt-0.5" />
                <span>Se team-samarbeid i praksis</span>
              </li>
              <li className="flex items-start gap-2 text-sm text-neutral-300">
                <FileText className="w-4 h-4 text-violet-400 flex-shrink-0 mt-0.5" />
                <span>Dokumentanalyse allerede kjørt</span>
              </li>
              <li className="flex items-start gap-2 text-sm text-neutral-300">
                <Zap className="w-4 h-4 text-violet-400 flex-shrink-0 mt-0.5" />
                <span>2-3 min guided tour (valgfritt)</span>
              </li>
            </ul>

            <div className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg font-semibold transition-colors ${
              selectedChoice === 'demo'
                ? 'bg-white text-neutral-900'
                : 'bg-white/10 text-white group-hover:bg-white/20'
            }`}>
              Start med demo
              <Play className="w-4 h-4" />
            </div>
          </button>

          {/* Scratch Choice */}
          <button
            onClick={() => handleChoice('scratch')}
            className={`relative group p-6 rounded-xl border transition-all duration-300 text-left hover:scale-[1.02] ${
              selectedChoice === 'scratch'
                ? 'border-neutral-500/50 bg-neutral-500/10'
                : 'border-white/10 bg-white/[0.02] hover:border-white/20 hover:bg-white/[0.04]'
            }`}
          >
            <div className="flex items-start gap-4 mb-4">
              <div className="w-12 h-12 bg-neutral-700 rounded-xl flex items-center justify-center flex-shrink-0">
                <FileText className="w-6 h-6 text-white" />
              </div>
              <div>
                <h3 className="text-xl font-bold text-white mb-1">
                  📋 Start med tom konto
                </h3>
                <p className="text-sm text-neutral-400">
                  Bygg din egen organisasjon
                </p>
              </div>
            </div>

            <ul className="space-y-2.5 mb-4">
              <li className="flex items-start gap-2 text-sm text-neutral-300">
                <div className="w-1.5 h-1.5 bg-neutral-500 rounded-full flex-shrink-0 mt-2" />
                <span>Begynn fra scratch</span>
              </li>
              <li className="flex items-start gap-2 text-sm text-neutral-300">
                <div className="w-1.5 h-1.5 bg-neutral-500 rounded-full flex-shrink-0 mt-2" />
                <span>Opprett dine egne saker</span>
              </li>
              <li className="flex items-start gap-2 text-sm text-neutral-300">
                <div className="w-1.5 h-1.5 bg-neutral-500 rounded-full flex-shrink-0 mt-2" />
                <span>Inviter kollegaer selv</span>
              </li>
              <li className="flex items-start gap-2 text-sm text-neutral-300">
                <div className="w-1.5 h-1.5 bg-neutral-500 rounded-full flex-shrink-0 mt-2" />
                <span>Full kontroll fra dag én</span>
              </li>
            </ul>

            <div className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg font-semibold transition-colors ${
              selectedChoice === 'scratch'
                ? 'bg-white text-neutral-900'
                : 'bg-white/10 text-white group-hover:bg-white/20'
            }`}>
              Start fra tomt
              <FileText className="w-4 h-4" />
            </div>
          </button>
        </div>

        {/* Footer note */}
        <div className="mt-6 text-center">
          <p className="text-xs text-neutral-500">
            💡 Du kan alltid bytte mellom demo og ekte data senere
          </p>
        </div>
      </div>
    </div>
  );
};

export default SandboxChoiceModal;
